import classnames from 'classnames'

import { getQaSelector } from '../../../utils/common'
import { FloatingTooltip } from '../../Floating'
import {
  ICON_HEIGHT_8,
  ICON_HEIGHT_16,
  IconNG,
  IconWithTooltip,
} from '../../Icon'
import { Link } from '../../Link'
import { SymbolCounter } from '../../SymbolCounter'
import {
  QA_NAME_INPUT_CONTEXT,
  QA_NAME_INPUT_ERROR_DEFAULT,
} from '../Input.constants'
import { InputWrapError } from '../InputWrapError'
import { InputWrapHelp } from '../InputWrapHelp'
import { InputWrapLabel } from '../InputWrapLabel'

import * as styles from './InputWrap.module.css'

import AsteriskIcon from '../../../icons/asterisk.svg'
import InfoIcon from '../../../icons/info.svg'

/**
 * @property {boolean} [disabled]
 * disabled: the control is visually and functionally disabled
 *
 * @property {boolean} [blocked]
 * blocked: behaves like disabled (no clicks, no value changes),
 * but keeps the normal visual style (no opacity change) and
 * shows a "not-allowed" cursor. Unlike "readonly", it should not
 * handle clicks or focus changes at all.
 */
function InputWrap({
  blocked,
  children,
  className,
  disabled,
  error,
  helpText,
  info,
  inputId,
  label,
  link,
  maxLength,
  name,
  qaName,
  ref,
  required,
  tooltip,
  value,
  withCounter,
}) {
  const inputWrapClassName = classnames(
    styles.InputWrap,
    {
      [styles.blocked]: blocked,
      [styles.disabled]: disabled,
    },
    className
  )

  const showTopControl = label || required || info || withCounter || link

  const inputQaProps = getQaSelector({
    context: QA_NAME_INPUT_CONTEXT,
    def: QA_NAME_INPUT_CONTEXT,
    value: qaName || name,
  })

  const errorQaProps = getQaSelector({
    context: QA_NAME_INPUT_ERROR_DEFAULT,
    def: QA_NAME_INPUT_ERROR_DEFAULT,
    value: qaName || name,
  })

  return (
    <FloatingTooltip content={tooltip}>
      <div {...inputQaProps} className={inputWrapClassName} ref={ref}>
        {showTopControl ? (
          <div className={styles.topControl}>
            {label ? (
              <InputWrapLabel
                disabled={disabled}
                htmlFor={inputId}
                label={label}
              />
            ) : null}
            {required ? (
              <IconNG
                Icon={AsteriskIcon}
                className={classnames(styles.icon, styles.requiredIcon)}
                size={ICON_HEIGHT_8}
              />
            ) : null}
            {info ? (
              <IconWithTooltip
                Icon={InfoIcon}
                className={styles.icon}
                size={ICON_HEIGHT_16}
                tooltip={info}
              />
            ) : null}
            {withCounter ? (
              <SymbolCounter
                className={styles.symbolCounter}
                maxLength={maxLength}
                value={value}
              />
            ) : null}
            {link ? (
              <Link className={styles.link} to={link.to}>
                {link.text}
              </Link>
            ) : null}
          </div>
        ) : null}
        {children}
        {!disabled && error ? (
          <InputWrapError {...errorQaProps} text={error} />
        ) : null}
        {!error && helpText ? <InputWrapHelp text={helpText} /> : null}
      </div>
    </FloatingTooltip>
  )
}

export default InputWrap
